import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Undo2, ChevronLeft, ChevronRight, Users, Building2, Globe } from 'lucide-react';

const TARGET_OPTIONS = [
  { value: 'all', label: '전체', icon: Globe, color: 'sky' },
  { value: 'members', label: '일반회원', icon: Users, color: 'emerald' },
  { value: 'clients', label: '권리사', icon: Building2, color: 'rose' },
];

export default function AdminBulkMessagePage() {
  const [content, setContent] = useState('');
  const [targetGroup, setTargetGroup] = useState('all');
  const [sending, setSending] = useState(false);

  const [bulkList, setBulkList] = useState({ messages: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/message/bulk/list?page=${page}`);
      setBulkList(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleSend = async () => {
    if (!content.trim()) return alert('내용을 입력해주세요.');
    const target = TARGET_OPTIONS.find((t) => t.value === targetGroup);
    if (!window.confirm(`${target.label} 대상으로 메시지를 발송하시겠습니까?`)) return;
    setSending(true);
    try {
      const res = await axios.post('/api/message/bulk', { targetGroup, content: content.trim() });
      alert(`${res.data.recipientCount}명에게 메시지를 발송했습니다.`);
      setContent('');
      fetchList();
    } catch (err) {
      alert(err.response?.data?.errorMessage || '발송에 실패했습니다.');
    }
    setSending(false);
  };

  const handleUnsend = async (id) => {
    if (!window.confirm('발송을 취소하시겠습니까? 받은 사람들의 메시지가 삭제됩니다.')) return;
    try {
      await axios.put(`/api/message/bulk/${id}/unsend`);
      alert('발송이 취소되었습니다.');
      fetchList(bulkList.page);
    } catch (err) {
      alert(err.response?.data?.errorMessage || '취소에 실패했습니다.');
    }
  };

  const targetLabel = (group) => TARGET_OPTIONS.find((t) => t.value === group)?.label || group;

  return (
    <div className="space-y-8">
      {/* 발송 폼 */}
      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-white text-lg font-bold">일괄 메시지 발송</h2>
          <p className="text-slate-500 text-xs mt-1">모든 사용자 또는 특정 그룹에게 일괄 메시지를 보냅니다.</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* 대상 선택 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">대상 그룹</label>
            <div className="flex gap-2">
              {TARGET_OPTIONS.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => setTargetGroup(value)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    targetGroup === value
                      ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/30`
                      : 'bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]'
                  }`}>
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 내용 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">내용</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="메시지 내용을 입력하세요"
              rows={4}
              className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-amber-500/30 transition resize-none"
            />
          </div>

          <button
            onClick={handleSend}
            disabled={sending || !content.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors">
            <Send size={14} />
            {sending ? '발송 중...' : '발송하기'}
          </button>
        </div>
      </div>

      {/* 발송 이력 */}
      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-white text-lg font-bold">발송 이력</h2>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
            </div>
          ) : bulkList.messages.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">발송 이력이 없습니다.</div>
          ) : (
            bulkList.messages.map((msg) => (
              <div key={msg.id} className={`px-6 py-4 ${msg.unsent ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        msg.targetGroup === 'all' ? 'bg-sky-500/15 text-sky-400'
                          : msg.targetGroup === 'members' ? 'bg-emerald-500/15 text-emerald-400'
                          : 'bg-rose-500/15 text-rose-400'
                      }`}>
                        {targetLabel(msg.targetGroup)}
                      </span>
                      <span className="text-[11px] text-slate-500">{msg.recipientCount}명</span>
                      <span className="text-[11px] text-slate-600">
                        {new Date(msg.createdAt).toLocaleString('ko-KR')}
                      </span>
                      {msg.unsent && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-400">
                          취소됨
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-2">{msg.content}</p>
                  </div>
                  {!msg.unsent && (
                    <button
                      onClick={() => handleUnsend(msg.id)}
                      className="shrink-0 flex items-center gap-1 px-3 py-1.5 bg-rose-500/15 text-rose-400 text-xs font-semibold rounded-lg hover:bg-rose-500/25 transition-colors">
                      <Undo2 size={12} />
                      발송취소
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {bulkList.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-3 border-t border-white/[0.06]">
            <button disabled={bulkList.page <= 1} onClick={() => fetchList(bulkList.page - 1)}
              className="p-1 rounded text-slate-400 hover:text-white disabled:opacity-30"><ChevronLeft size={16} /></button>
            <span className="text-xs text-slate-500">{bulkList.page} / {bulkList.totalPages}</span>
            <button disabled={bulkList.page >= bulkList.totalPages} onClick={() => fetchList(bulkList.page + 1)}
              className="p-1 rounded text-slate-400 hover:text-white disabled:opacity-30"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
    </div>
  );
}
